import { cacheKeys } from "@constants/cache-keys";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import type { ProductType } from "@memorang/types/src/bundle";
import { useQuery } from "@tanstack/react-query";
import {
	fetchProductDetails,
	fetchProductMetadata,
} from "api/commerce/product-service";
import { useAppStore } from "./useAppStore";
import useClerkUser from "./useClerkUser";
export const useProductDetails = (
	productType: ProductType,
	explicitProductNumericId?: number,
) => {
	const {
		currentExamContext: {
			studyPackProductNumericId,
			practiceTestProductNumericId,
		},
	} = useExamContext();

	const getProductNumericId = () => {
		switch (productType) {
			case "STUDY_PACK":
				return studyPackProductNumericId;
			case "PRACTICE_TEST":
				return practiceTestProductNumericId;
			default:
				return undefined;
		}
	};
	const tenantId = useAppStore((state) => state.tenant.id);
	const numericId = explicitProductNumericId || getProductNumericId();

	return useQuery({
		queryKey: [cacheKeys.productDetails, numericId, tenantId],
		queryFn: async () => {
			return await fetchProductDetails({
				tenantId: tenantId,
				numericId: numericId!,
			});
		},
		enabled: !!tenantId && !!numericId,
	});
};

export const useProductMetadata = (
	productType: ProductType,
	explicitProductId?: string,
) => {
	const {
		currentExamContext: { studyPackProductId, practiceTestProductId },
	} = useExamContext();

	const getProductId = () => {
		switch (productType) {
			case "STUDY_PACK":
				return studyPackProductId;
			case "PRACTICE_TEST":
				return practiceTestProductId;
			default:
				return undefined;
		}
	};
	const tenantId = useAppStore((state) => state.tenant.id);

	const { userLogicalId } = useClerkUser();
	const productId = explicitProductId || getProductId();

	return useQuery({
		queryKey: [cacheKeys.productMetadata, productId],
		queryFn: async () => {
			const result = await fetchProductMetadata({
				tenantId: tenantId,
				productId: productId!,
				userId: userLogicalId,
			});
			return result;
		},
		enabled: !!userLogicalId && !!productId,
	});
};
